import moment from "moment";

import {computeGoalkeeperMatchMetrics} from "@/utils/goalkeeperMatchMetrics";
import {GOALKEEPER_KPI_CONFIG} from "@/utils/goalkeeperKpiConfig";
import {dateFormat} from "@/utils/constants";

let jsPDF;
try {
	jsPDF = require("jspdf").default || require("jspdf");
} catch (error) {
	console.error("jsPDF non è installato. Esegui: npm install jspdf");
	jsPDF = null;
}

let html2canvas;
try {
	html2canvas = require("html2canvas");
} catch (error) {
	console.error(
		"html2canvas non è installato. Esegui: npm install html2canvas",
	);
	html2canvas = null;
}

const getTextLinesHeight = (
	linesCount,
	fontSize,
	lineHeightMultiplier = 1.35,
) => linesCount * fontSize * lineHeightMultiplier;

const formatOptionalRatioPct = (ratioValue, decimals = 0, naPlaceholder) => {
	if (ratioValue === null) return naPlaceholder;
	return `${(ratioValue * 100).toFixed(decimals)}%`;
};

const getKpiStatusFromConfig = (kpiKey, rawValue, kpiConfig) => {
	const config = kpiConfig[kpiKey];
	if (!config) return "unavailable";
	if (rawValue === null) return "unavailable";

	if (config.type === "score10") {
		if (rawValue >= config.thresholds.good) return "green";
		if (rawValue >= config.thresholds.avg) return "yellow";
		return "red";
	}

	if (config.type === "ratio") {
		if (rawValue >= config.thresholds.good) return "green";
		if (rawValue >= config.thresholds.avg) return "yellow";
		return "red";
	}

	// ratioLowerIsBetter
	if (rawValue <= config.thresholds.good) return "green";
	if (rawValue <= config.thresholds.avg) return "yellow";
	return "red";
};

/**
 * Generate a multi-page A4 PDF for the Goalkeeper Match Report detail page.
 *
 * IMPORTANT:
 * - Keeps KPI logic and metrics consistent with the UI (reuses computeGoalkeeperMatchMetrics + GOALKEEPER_KPI_CONFIG).
 * - Only shot-map is captured from DOM via html2canvas; all text blocks are drawn with proper wrapping.
 */
export const generateGoalkeeperMatchReportPDF = async ({
	reportData,
	t,
	shotMapGoalElement,
	fileNamePrefix,
}) => {
	if (!jsPDF) {
		throw new Error("jsPDF non è installato. Esegui: npm install jspdf");
	}
	if (!html2canvas) {
		throw new Error(
			"html2canvas non è installato. Esegui: npm install html2canvas",
		);
	}

	const doc = new jsPDF({unit: "pt", format: "a4"});
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();

	const SP = {
		pagePad: 42,
		blockGap: 12,
		cardPad: 12,
		footerH: 50,
	};

	const theme = {
		primary: [16, 89, 191],
		primaryDark: [9, 53, 128],
		primaryLight: [59, 130, 246],
		bg: [245, 247, 250],
		border: [229, 231, 235],
		text: [30, 41, 59],
		muted: [100, 116, 139],
	};

	const naPlaceholder =
		t?.("goalkeeper_report.common.na_placeholder") ||
		t?.("goalkeeper_report.common.numeric_placeholder") ||
		"Dato non disponibile";

	const safeText = (v, fallback = naPlaceholder) =>
		v === null || v === undefined || v === "" ? fallback : String(v);

	let yPosition = 0;

	const checkPageBreak = (requiredHeight) => {
		if (yPosition + requiredHeight > pageHeight - SP.footerH) {
			doc.addPage();
			yPosition = SP.pagePad;
			doc.setTextColor(...theme.text);
		}
	};

	const setFont = (size, style = "normal") => {
		doc.setFont("helvetica", style);
		doc.setFontSize(size);
	};

	const wrapText = (text, maxWidth, fontSize = 11) => {
		setFont(fontSize, "normal");
		return doc.splitTextToSize(safeText(text, ""), maxWidth);
	};

	const drawCard = (x, y, w, h, {radius = 12, shadow = true} = {}) => {
		if (shadow) {
			doc.setFillColor(0, 0, 0);
			try {
				doc.setGState(new doc.GState({opacity: 0.06}));
			} catch (_) {
				// ignore
			}
			doc.roundedRect(x + 2, y + 3, w, h, radius, radius, "F");
			try {
				doc.setGState(new doc.GState({opacity: 1}));
			} catch (_) {
				// ignore
			}
		}

		doc.setDrawColor(...theme.border);
		doc.setLineWidth(0.8);
		doc.setFillColor(255, 255, 255);
		doc.roundedRect(x, y, w, h, radius, radius, "FD");
	};

	const drawSectionHeader = (title) => {
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;
		// Leave enough space so the title is not orphaned at the bottom of a page.
		checkPageBreak(60);

		// pill + dot (like other app PDFs)
		doc.setFillColor(...theme.primary);
		doc.roundedRect(x, yPosition, w, 22, 10, 10, "F");
		doc.setFillColor(...theme.primaryLight);
		doc.circle(x + 12, yPosition + 11, 4, "F");
		doc.setFont("helvetica", "bold");
		doc.setFontSize(12);
		doc.setTextColor(255, 255, 255);
		doc.text(title, x + 22, yPosition + 15);
		yPosition += 22 + 10;
		doc.setTextColor(...theme.text);
	};

	const drawSubSectionLabel = (label) => {
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;
		checkPageBreak(26);

		doc.setFillColor(...theme.bg);
		doc.setDrawColor(...theme.border);
		doc.setLineWidth(0.8);
		doc.roundedRect(x, yPosition, w, 18, 9, 9, "FD");

		doc.setFont("helvetica", "bold");
		doc.setFontSize(10);
		doc.setTextColor(...theme.primaryDark);
		doc.text(safeText(label, ""), x + 12, yPosition + 13);

		yPosition += 18 + 6;
		doc.setTextColor(...theme.text);
	};

	const drawKeyValueBlock = ({items, cols = 2}) => {
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;
		const colW = (w - SP.blockGap * (cols - 1)) / cols;
		const rowGap = 8;

		const cardInnerPad = SP.cardPad;
		const valueFontSize = 11;
		const labelFontSize = 9;

		// Estimate height first: each item is a label+wrapped value.
		const renderedItems = (items || []).map((it) => {
			const maxValueW = colW - cardInnerPad * 2;
			const lines = wrapText(it.value, maxValueW, valueFontSize);
			return {...it, _lines: lines};
		});

		const rows = Math.ceil(renderedItems.length / cols);
		let h = 0;
		for (let r = 0; r < rows; r++) {
			const rowItems = renderedItems.slice(r * cols, r * cols + cols);
			const rowH = Math.max(
				...rowItems.map(
					(ri) =>
						getTextLinesHeight(ri._lines.length, valueFontSize, 1.25) + 24,
				),
				0,
			);
			h += rowH + rowGap;
		}
		h += SP.cardPad * 2;

		checkPageBreak(h);
		drawCard(x, yPosition, w, h, {radius: 12, shadow: true});

		let cursorY = yPosition + SP.cardPad;
		doc.setTextColor(...theme.muted);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(labelFontSize);

		for (let r = 0; r < rows; r++) {
			const rowItems = renderedItems.slice(r * cols, r * cols + cols);
			const rowH = Math.max(
				...rowItems.map(
					(ri) =>
						getTextLinesHeight(ri._lines.length, valueFontSize, 1.25) + 24,
				),
				0,
			);

			for (let idx = 0; idx < rowItems.length; idx++) {
				const ri = rowItems[idx];
				const colX = x + idx * (colW + SP.blockGap);
				const labelX = colX + cardInnerPad;
				const valueX = colX + cardInnerPad;
				const labelY = cursorY + 12;
				doc.text(`${safeText(ri.label, "")}:`, labelX, labelY);

				doc.setTextColor(...theme.text);
				setFont(valueFontSize, "normal");
				const textY = labelY + 10;
				doc.text(ri._lines, valueX, textY);
				doc.setTextColor(...theme.muted);
				setFont(labelFontSize, "bold");
			}

			cursorY += rowH + rowGap;
			doc.setTextColor(...theme.muted);
			setFont(labelFontSize, "bold");
		}

		yPosition += h + SP.blockGap;
	};

	const drawTextBox = ({title, text, heightHint = null}) => {
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;
		const padding = SP.cardPad;
		const titleFont = 10;
		const bodyFont = 11;
		const maxTextW = w - padding * 2;
		const lines = wrapText(text, maxTextW, bodyFont);
		const bodyH = getTextLinesHeight(lines.length, bodyFont, 1.3);
		const boxH = heightHint ?? padding * 2 + 18 + bodyH;

		checkPageBreak(boxH + SP.blockGap);
		drawCard(x, yPosition, w, boxH, {radius: 12, shadow: false});

		doc.setFont("helvetica", "bold");
		doc.setFontSize(titleFont);
		doc.setTextColor(...theme.primaryDark);
		doc.text(title, x + padding, yPosition + padding + 14);

		doc.setTextColor(...theme.text);
		setFont(bodyFont, "normal");
		doc.text(lines, x + padding, yPosition + padding + 30);

		yPosition += boxH + SP.blockGap;
	};

	const drawKpiCard = ({label, value, interpretation}) => {
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		const padding = 10;
		const valueFont = 16;
		const labelFont = 9;
		const interpFont = 10;
		const maxW = w - padding * 2;

		const interpLines = wrapText(interpretation, maxW, interpFont);
		const boxH =
			padding * 2 +
			9 + // header
			18 + // value line area
			getTextLinesHeight(interpLines.length, interpFont, 1.15) +
			4;

		checkPageBreak(boxH + SP.blockGap);
		drawCard(x, yPosition, w, boxH, {radius: 12, shadow: true});

		doc.setTextColor(...theme.primaryDark);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(labelFont);
		doc.text(label, x + padding, yPosition + padding + 10);

		doc.setTextColor(...theme.text);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(valueFont);
		doc.text(
			safeText(value, naPlaceholder),
			x + padding,
			yPosition + padding + 38,
		);

		doc.setTextColor(...theme.text);
		setFont(interpFont, "normal");
		doc.text(interpLines, x + padding, yPosition + padding + 52);

		yPosition += boxH + SP.blockGap;
	};

	const drawCoachNotes = () => {
		drawTextBox({
			title: safeText(t?.("goalkeeper_report.view_fields.general_opinion"), ""),
			text: reportData?.generalOpinion || naPlaceholder,
		});

		// Goals conceded descriptions (up to 6 entries).
		const descriptions = (
			reportData?.goalDescriptions ??
			[1, 2, 3, 4, 5, 6].map((n) => reportData?.[`descriptionGoalConceded${n}`])
		).filter(Boolean);

		const concededTitle = safeText(
			t?.("goalkeeper_report.report_info.conceded_goals_description"),
			"",
		);
		drawSubSectionLabel(concededTitle);

		// Render each description as a numbered card.
		const maxTextW = pageWidth - SP.pagePad * 2 - SP.cardPad * 2;
		const fontSize = 11;
		const lineH = 1.3;
		const badgeW = 18;
		const leftPadding = SP.cardPad;

		if (!descriptions.length) {
			checkPageBreak(30);
			drawTextBox({
				title: "",
				text:
					t?.("goalkeeper_report.report_info.no_conceded_goals_description") ||
					naPlaceholder,
				heightHint: 60,
			});
		} else {
			descriptions.forEach((d, idx) => {
				const lines = wrapText(d, maxTextW - badgeW - 8, fontSize);
				const h =
					SP.cardPad * 2 +
					Math.max(28, getTextLinesHeight(lines.length, fontSize, lineH)) +
					12;
				checkPageBreak(h);
				const x = SP.pagePad;
				const w = pageWidth - SP.pagePad * 2;
				drawCard(x, yPosition, w, h, {radius: 12, shadow: false});

				// badge
				doc.setFillColor(...theme.primaryLight);
				doc.circle(
					x + leftPadding + badgeW / 2,
					yPosition + SP.cardPad + 10,
					8,
					"F",
				);
				doc.setFillColor(255, 255, 255);
				doc.setTextColor(255, 255, 255);
				doc.setFont("helvetica", "bold");
				doc.setFontSize(9);
				doc.text(
					String(idx + 1),
					x + leftPadding + badgeW / 2,
					yPosition + SP.cardPad + 14,
					{
						align: "center",
					},
				);

				doc.setTextColor(...theme.text);
				setFont(fontSize, "normal");
				doc.text(
					lines,
					x + leftPadding + badgeW + 12,
					yPosition + SP.cardPad + 18,
				);

				yPosition += h + SP.blockGap;
			});
		}

		drawTextBox({
			title: safeText(t?.("goalkeeper_report.coach_notes.free_notes"), ""),
			text: reportData?.reportText?.length
				? reportData.reportText
				: t?.("goalkeeper_report.coach_notes.no_free_notes") || naPlaceholder,
		});
	};

	const captureShotMapToImage = async () => {
		if (!shotMapGoalElement) return null;
		const rect = shotMapGoalElement.getBoundingClientRect();
		const width = Math.max(1, Math.round(rect.width));
		const height = Math.max(1, Math.round(rect.height));
		const canvas = await html2canvas(shotMapGoalElement, {
			backgroundColor: "#ffffff",
			scale: 2,
			useCORS: true,
			logging: false,
			width,
			height,
		});
		return canvas;
	};

	// =========================
	// Compute analytics (same as UI)
	// =========================
	const {
		shotsOnTargetCount,
		hasPerShotOutcomes,
		goalsConcededValue,
		finalRatingValue,
		savesOnTargetValue,
		shotStoppingIndexRatio,
		distributionEfficiencyRatio,
		aerialControlRatio,
		setPieceControlRatio,
		errorImpactRatio,
	} = computeGoalkeeperMatchMetrics(reportData);

	const shotStoppingIndexDisplay = formatOptionalRatioPct(
		shotStoppingIndexRatio,
		0,
		naPlaceholder,
	);
	const distributionEfficiencyDisplay = formatOptionalRatioPct(
		distributionEfficiencyRatio,
		0,
		naPlaceholder,
	);
	const aerialControlDisplay = formatOptionalRatioPct(
		aerialControlRatio,
		0,
		naPlaceholder,
	);
	const setPieceControlDisplay = formatOptionalRatioPct(
		setPieceControlRatio,
		0,
		naPlaceholder,
	);
	const errorImpactDisplay = formatOptionalRatioPct(
		errorImpactRatio,
		0,
		naPlaceholder,
	);

	const finalRatingDisplay =
		finalRatingValue !== null && finalRatingValue !== undefined
			? finalRatingValue.toFixed(2)
			: naPlaceholder;

	const finalRatingStatus = getKpiStatusFromConfig(
		"finalRating",
		finalRatingValue,
		GOALKEEPER_KPI_CONFIG,
	);
	const shotStoppingIndexStatus = getKpiStatusFromConfig(
		"shotStopping",
		shotStoppingIndexRatio,
		GOALKEEPER_KPI_CONFIG,
	);
	const errorImpactStatus = getKpiStatusFromConfig(
		"errorImpact",
		errorImpactRatio,
		GOALKEEPER_KPI_CONFIG,
	);

	const getKpiInterpretationFromConfig = (kpiKey, rawValue) => {
		const config = GOALKEEPER_KPI_CONFIG[kpiKey];
		if (!config) return naPlaceholder;
		if (rawValue === null) return naPlaceholder;

		const status = getKpiStatusFromConfig(
			kpiKey,
			rawValue,
			GOALKEEPER_KPI_CONFIG,
		);
		const semantic =
			status === "green" ? "good" : status === "yellow" ? "avg" : "poor";
		return (
			t?.(`goalkeeper_report.kpi_interpretation.${kpiKey}.${semantic}`) ||
			config.interpretation?.[semantic] ||
			naPlaceholder
		);
	};

	const finalRatingInterpretation = getKpiInterpretationFromConfig(
		"finalRating",
		finalRatingValue,
	);
	const shotStoppingInterpretation = getKpiInterpretationFromConfig(
		"shotStopping",
		shotStoppingIndexRatio,
	);
	const distributionInterpretation = getKpiInterpretationFromConfig(
		"distribution",
		distributionEfficiencyRatio,
	);
	const aerialInterpretation = getKpiInterpretationFromConfig(
		"aerialControl",
		aerialControlRatio,
	);
	const setPiecesInterpretation = getKpiInterpretationFromConfig(
		"setPieces",
		setPieceControlRatio,
	);
	const errorImpactInterpretation = getKpiInterpretationFromConfig(
		"errorImpact",
		errorImpactRatio,
	);

	// Shot map quick interpretation (same as UI)
	const shotMapInsight =
		shotStoppingIndexRatio === null
			? t?.("goalkeeper_report.shot_map_insight.unavailable") || naPlaceholder
			: shotStoppingIndexRatio >=
				  GOALKEEPER_KPI_CONFIG.shotStopping.thresholds.good
				? t?.("goalkeeper_report.shot_map_insight.high_efficiency") ||
					naPlaceholder
				: shotStoppingIndexRatio >=
					  GOALKEEPER_KPI_CONFIG.shotStopping.thresholds.avg
					? t?.("goalkeeper_report.shot_map_insight.avg_performance") ||
						naPlaceholder
					: t?.("goalkeeper_report.shot_map_insight.insufficient") ||
						naPlaceholder;

	const matchSummaryInsight = (() => {
		const parts = [];
		if (finalRatingStatus === "green")
			parts.push(t?.("goalkeeper_report.match_summary.rating.good"));
		else if (finalRatingStatus === "yellow")
			parts.push(t?.("goalkeeper_report.match_summary.rating.avg"));
		else if (finalRatingStatus === "red")
			parts.push(t?.("goalkeeper_report.match_summary.rating.poor"));

		if (shotStoppingIndexStatus === "green")
			parts.push(t?.("goalkeeper_report.match_summary.stopping.good"));
		else if (shotStoppingIndexStatus === "yellow")
			parts.push(t?.("goalkeeper_report.match_summary.stopping.avg"));
		else if (shotStoppingIndexStatus === "red")
			parts.push(t?.("goalkeeper_report.match_summary.stopping.poor"));

		if (errorImpactStatus === "green")
			parts.push(t?.("goalkeeper_report.match_summary.error.good"));
		else if (errorImpactStatus === "yellow")
			parts.push(t?.("goalkeeper_report.match_summary.error.avg"));
		else if (errorImpactStatus === "red")
			parts.push(t?.("goalkeeper_report.match_summary.error.poor"));

		return parts.filter(Boolean).join(" ");
	})();

	const shotsOnTargetFacedDisplay =
		shotsOnTargetCount > 0 ? shotsOnTargetCount : naPlaceholder;
	const savesDisplay =
		savesOnTargetValue !== null ? savesOnTargetValue : naPlaceholder;
	const goalsConcededDisplay =
		goalsConcededValue !== null ? goalsConcededValue : naPlaceholder;
	const savePercentageDisplay = shotStoppingIndexDisplay;

	// =========================
	// HEADER
	// =========================
	const headerH = 92;
	doc.setFillColor(...theme.primaryDark);
	doc.rect(0, 0, pageWidth, headerH, "F");
	doc.setFillColor(...theme.primary);
	doc.rect(0, headerH - 20, pageWidth, 20, "F");
	doc.setFillColor(...theme.primaryLight);
	doc.rect(0, headerH - 3, pageWidth, 3, "F");

	const headerTitle =
		t?.("goalkeeper_report.pdf.main_title") ||
		t?.("goalkeeper_report.export_title") ||
		"Report gara portiere";
	doc.setFont("helvetica", "bold");
	doc.setFontSize(18);
	doc.setTextColor(255, 255, 255);
	doc.text(headerTitle, SP.pagePad, 44);

	const goalkeeperName = reportData?.player
		? `${reportData?.player?.firstName ?? ""} ${reportData?.player?.lastName ?? ""}`.trim()
		: "";
	const dateText = reportData?.reportDate
		? moment(reportData.reportDate).format(dateFormat)
		: "";
	doc.setFont("helvetica", "normal");
	doc.setFontSize(10);
	doc.setTextColor(235, 245, 255);
	const matchReference = reportData?.match
		? `${moment(reportData?.match?.startDate).format(dateFormat)} - ${reportData?.match?.homeClub} - ${reportData?.match?.awayClub}`
		: "";
	doc.text(
		[goalkeeperName, dateText, matchReference].filter(Boolean).join(" • "),
		SP.pagePad,
		66,
	);

	yPosition = headerH + 20;
	doc.setTextColor(...theme.text);

	// =========================
	// Header match summary (compact)
	// =========================
	drawKeyValueBlock({
		items: [
			{
				label: t?.("goalkeeper_report.header.coach"),
				value: reportData?.reportAuthor || naPlaceholder,
			},
			{
				label: t?.("goalkeeper_report.view_fields.minutes_played"),
				value:
					reportData?.minutesPlayed !== null &&
					reportData?.minutesPlayed !== undefined
						? `${reportData?.minutesPlayed}'`
						: naPlaceholder,
			},
			{
				label: t?.("goalkeeper_report.view_fields.goals_conceded"),
				value:
					reportData?.totalGoalsConceded !== null &&
					reportData?.totalGoalsConceded !== undefined
						? reportData?.totalGoalsConceded
						: naPlaceholder,
			},
			{
				label: t?.("goalkeeper_report.kpi.final_rating"),
				value: finalRatingDisplay,
			},
		],
		cols: 2,
	});

	// =========================
	// KPI SUMMARY
	// =========================
	drawSectionHeader(
		t?.("goalkeeper_report.pdf.kpi_section_title") || "Indicatori",
	);

	drawKpiCard({
		label: t?.("goalkeeper_report.kpi.final_rating"),
		value: finalRatingDisplay,
		interpretation: finalRatingInterpretation,
	});
	drawKpiCard({
		label: t?.("goalkeeper_report.kpi.shot_stopping"),
		value: shotStoppingIndexDisplay,
		interpretation: shotStoppingInterpretation,
	});
	drawKpiCard({
		label: t?.("goalkeeper_report.kpi.distribution"),
		value: distributionEfficiencyDisplay,
		interpretation: distributionInterpretation,
	});
	drawKpiCard({
		label: t?.("goalkeeper_report.kpi.aerial_control"),
		value: aerialControlDisplay,
		interpretation: aerialInterpretation,
	});
	drawKpiCard({
		label: t?.("goalkeeper_report.kpi.set_piece_control"),
		value: setPieceControlDisplay,
		interpretation: setPiecesInterpretation,
	});
	drawKpiCard({
		label: t?.("goalkeeper_report.kpi.error_impact"),
		value: errorImpactDisplay,
		interpretation: errorImpactInterpretation,
	});

	drawTextBox({
		title: t?.("goalkeeper_report.match_summary.title"),
		text: matchSummaryInsight || naPlaceholder,
	});

	// =========================
	// Shot map section
	// =========================
	drawSectionHeader(t?.("goalkeeper_report.sections.shot_map"));

	// Capture shot map image from DOM
	let shotMapCanvas = null;
	try {
		shotMapCanvas = await captureShotMapToImage();
	} catch (e) {
		console.error("Errore cattura shot map:", e);
	}

	const cardX = SP.pagePad;
	const cardW = pageWidth - SP.pagePad * 2;
	const hasShots = shotsOnTargetCount > 0;
	const imgMaxW = Math.min(hasShots ? 260 : 220, cardW);

	let imgW = imgMaxW;
	let imgH = imgMaxW * (2 / 3); // 3/2 aspect => h = w*2/3
	if (shotMapCanvas) {
		const aspect = shotMapCanvas.width / shotMapCanvas.height;
		imgW = Math.min(imgMaxW, cardW);
		imgH = imgW / aspect;
	}

	// Insight: compute wrapped lines so card height is accurate for long texts.
	const insightTitle = t?.("goalkeeper_report.shot_map_insight.title");
	const insightText = hasShots
		? shotMapInsight || naPlaceholder
		: t?.("goalkeeper_report.shot_map_empty_state") || naPlaceholder;
	const maxInsightW = cardW - SP.cardPad * 2;
	const insightLines = wrapText(insightText, maxInsightW, 11);
	const insightTextH = getTextLinesHeight(insightLines.length, 11, 1.25);
	// Card contents:
	// - image (imgH)
	// - mini KPI 2x2 grid
	// - insight title + wrapped insight text
	const mapCardH = imgH + SP.cardPad * 2 + 98 + insightTextH;
	checkPageBreak(mapCardH + SP.blockGap);
	drawCard(cardX, yPosition, cardW, mapCardH, {radius: 12, shadow: true});

	// Image
	if (shotMapCanvas) {
		const imgData = shotMapCanvas.toDataURL("image/png");
		const imgX = cardX + (cardW - imgW) / 2;
		const imgY = yPosition + SP.cardPad;
		doc.addImage(imgData, "PNG", imgX, imgY, imgW, imgH);
	} else {
		// Fallback: keep layout stable even if html2canvas fails.
		doc.setDrawColor(...theme.border);
		doc.setLineWidth(1);
		doc.roundedRect(
			cardX + (cardW - imgW) / 2,
			yPosition + SP.cardPad,
			imgW,
			imgH,
			10,
			10,
			"S",
		);
		doc.setFont("helvetica", "italic");
		doc.setFontSize(10);
		doc.setTextColor(...theme.muted);
		doc.text(
			naPlaceholder,
			cardX + cardW / 2,
			yPosition + SP.cardPad + imgH / 2,
			{align: "center"},
		);
	}

	// Summary line (mini KPI)
	doc.setFont("helvetica", "bold");
	doc.setFontSize(10);
	doc.setTextColor(...theme.primaryDark);
	// not visible as label; we use mini lines

	const miniFont = 10;
	const miniLabelFont = 8;
	const contentW = cardW - SP.cardPad * 2;
	const miniRowY = yPosition + SP.cardPad + imgH + 10;

	const miniBoxW = (contentW - 10) / 2;
	const miniBoxH = 32;
	const miniY1 = miniRowY;
	const miniY2 = miniRowY + miniBoxH + 6;
	const miniX1 = cardX + SP.cardPad;
	const miniX2 = miniX1 + miniBoxW + 10 - 0;

	const miniDefs = [
		{
			label: t?.("goalkeeper_report.shot_map_summary.abbr_shots_faced"),
			value: shotsOnTargetFacedDisplay,
		},
		{
			label: t?.("goalkeeper_report.shot_map_summary.abbr_saves"),
			value: savesDisplay,
		},
		{
			label: t?.("goalkeeper_report.shot_map_summary.abbr_goals"),
			value: goalsConcededDisplay,
		},
		{
			label: t?.("goalkeeper_report.shot_map_summary.abbr_save_pct"),
			value: savePercentageDisplay,
		},
	];

	const drawMiniBox = (x, y, def) => {
		doc.setDrawColor(...theme.border);
		doc.setFillColor(255, 255, 255);
		doc.roundedRect(x, y, miniBoxW, miniBoxH, 10, 10, "FD");
		doc.setFont("helvetica", "bold");
		doc.setFontSize(miniLabelFont);
		doc.setTextColor(...theme.muted);
		doc.text(`${safeText(def.label, "")}`, x + 10, y + 14);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(miniFont);
		doc.setTextColor(...theme.text);
		doc.text(`${safeText(def.value, naPlaceholder)}`, x + 10, y + 28);
	};

	drawMiniBox(miniX1, miniY1, miniDefs[0]);
	drawMiniBox(miniX2, miniY1, miniDefs[1]);
	drawMiniBox(miniX1, miniY2, miniDefs[2]);
	drawMiniBox(miniX2, miniY2, miniDefs[3]);

	// Insight
	const insightY = miniY2 + miniBoxH + 14;

	doc.setFont("helvetica", "bold");
	doc.setFontSize(10);
	doc.setTextColor(...theme.primaryDark);
	doc.text(insightTitle || "", cardX + SP.cardPad, insightY);

	doc.setFont("helvetica", "normal");
	doc.setFontSize(11);
	doc.setTextColor(...theme.text);
	doc.text(insightLines, cardX + SP.cardPad, insightY + 14);

	yPosition += mapCardH + SP.blockGap;

	// Legend (simple text)
	if (hasPerShotOutcomes) {
		checkPageBreak(70);
		drawTextBox({
			title: t?.("goalkeeper_report.legend_shot_outcomes"),
			text: [
				`• ${t?.("goalkeeper_report.legend_saved")}`,
				`• ${t?.("goalkeeper_report.legend_goal")}`,
				`• ${t?.("goalkeeper_report.legend_shot_unknown")}`,
			].join("\n"),
		});
	}

	// =========================
	// Offensive phase
	// =========================
	const drawParameterRow = ({label, okLabel, failedLabel}) => {
		const ok = parseInt(reportData?.[okLabel] || 0, 10);
		const failed = parseInt(reportData?.[failedLabel] || 0, 10);
		const total = ok + failed;

		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		const padding = SP.cardPad;
		const labelFont = 10;
		const valueFont = 11;
		const maxLabelW = w * 0.44;

		const labelLines = wrapText(label, maxLabelW, labelFont);
		const h = padding * 2 + Math.max(30, labelLines.length * 13) + 16;

		checkPageBreak(h);
		drawCard(x, yPosition, w, h, {radius: 12, shadow: false});

		// columns
		const col1X = x + padding;
		const col2X = x + padding + w * 0.46;

		doc.setFont("helvetica", "bold");
		doc.setFontSize(10);
		doc.setTextColor(...theme.primaryDark);
		doc.text(labelLines, col1X, yPosition + padding + 12);

		doc.setFont("helvetica", "bold");
		doc.setFontSize(valueFont);
		doc.setTextColor(...theme.text);
		doc.text(String(total), col2X, yPosition + padding + 18);

		// ok/failed mini line
		const yesLabel = t?.("goalkeeper_report.common.yes") || "Sì";
		const noLabel = t?.("goalkeeper_report.common.no") || "No";
		doc.setFont("helvetica", "normal");
		doc.setFontSize(10);
		doc.setTextColor(...theme.muted);
		doc.text(`${yesLabel}: ${ok}`, col2X, yPosition + padding + 34);
		doc.text(`${noLabel}: ${failed}`, col2X, yPosition + padding + 49);

		yPosition += h + SP.blockGap;
	};

	drawSectionHeader(
		t?.("goalkeeper_report.sections.offensive_phase") || "Fase offensiva",
	);
	drawParameterRow({
		label: t?.("goalkeeper_report.offensive.short_pass") || "Passaggio corto",
		okLabel: "shortPassOk",
		failedLabel: "shortPassFailed",
	});
	drawParameterRow({
		label:
			t?.("goalkeeper_report.offensive.long_throw") || "Lancio medio e lungo",
		okLabel: "longMidThrowOk",
		failedLabel: "longMidThrowFailed",
	});
	drawParameterRow({
		label:
			t?.("goalkeeper_report.offensive.restart_with_hands") ||
			"Ripartenza con le mani",
		okLabel: "restartWithHandsOk",
		failedLabel: "restartWithHandsFailed",
	});
	drawParameterRow({
		label:
			t?.("goalkeeper_report.offensive.restart_with_fly_remittal") ||
			"Ripartenza con rinvio al volo",
		okLabel: "remittalOnTheFlyOk",
		failedLabel: "remittalOnTheFlyFailed",
	});
	drawParameterRow({
		label:
			t?.("goalkeeper_report.offensive.positive_transition_outside") ||
			"Transizione positiva fuori area",
		okLabel: "positiveTransitionOutsideOk",
		failedLabel: "positiveTransitionOutsideFailed",
	});

	// =========================
	// Defensive phase
	// =========================
	drawSectionHeader(
		t?.("goalkeeper_report.sections.defensive_phase") || "Fase difensiva",
	);
	drawParameterRow({
		label: t?.("goalkeeper_report.defensive.save_grip") || "Parata in presa",
		okLabel: "saveGripOk",
		failedLabel: "saveGripFailed",
	});
	drawParameterRow({
		label:
			t?.("goalkeeper_report.defensive.save_rebound_deviation") ||
			"Parata in respinta/deviazione",
		okLabel: "saveReboundOk",
		failedLabel: "saveReboundFailed",
	});
	drawParameterRow({
		label: t?.("goalkeeper_report.defensive.high_claim") || "Uscita alta presa",
		okLabel: "highClaimOk",
		failedLabel: "highClaimFailed",
	});
	drawParameterRow({
		label:
			t?.("goalkeeper_report.defensive.high_punch") ||
			"Uscita alta deviazione/respinta",
		okLabel: "highPunchOk",
		failedLabel: "highPunchFailed",
	});
	drawParameterRow({
		label:
			t?.("goalkeeper_report.defensive.low_dive") ||
			"Uscita bassa/attacco palla",
		okLabel: "lowDiveOk",
		failedLabel: "lowDiveFailed",
	});
	drawParameterRow({
		label:
			t?.("goalkeeper_report.defensive.depth_coverage_inside_area") ||
			"Copertura profondità area",
		okLabel: "depthCoverageInsideOk",
		failedLabel: "depthCoverageInsideFailed",
	});
	drawParameterRow({
		label:
			t?.("goalkeeper_report.defensive.depth_coverage_outside_area") ||
			"Copertura profondità fuori area",
		okLabel: "depthCoverageOutsideOk",
		failedLabel: "depthCoverageOutsideFailed",
	});

	// =========================
	// Set pieces (corners out/in + metrics)
	// =========================
	const drawViewFieldGrid = ({items}) => {
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;
		const cols = 2;
		const colW = (w - SP.blockGap * (cols - 1)) / cols;
		const padding = SP.cardPad;
		const labelFont = 9;
		const valueFont = 11;

		const rendered = (items || []).map((it) => {
			const maxValueW = colW - padding * 2;
			const lines = wrapText(it.value, maxValueW, valueFont);
			return {...it, _lines: lines};
		});

		const rows = Math.ceil(rendered.length / cols);

		let h = 0;
		const rowGap = 6;
		for (let r = 0; r < rows; r++) {
			const rowItems = rendered.slice(r * cols, r * cols + cols);
			const rowH = Math.max(
				...rowItems.map(
					(ri) => getTextLinesHeight(ri._lines.length, valueFont, 1.1) + 26,
				),
			);
			h += rowH + rowGap;
		}
		h += padding * 2;

		checkPageBreak(h);
		drawCard(x, yPosition, w, h, {radius: 12, shadow: false});

		let cursorY = yPosition + padding;
		for (let r = 0; r < rows; r++) {
			const rowItems = rendered.slice(r * cols, r * cols + cols);
			const rowH = Math.max(
				...rowItems.map(
					(ri) => getTextLinesHeight(ri._lines.length, valueFont, 1.1) + 26,
				),
			);

			for (let idx = 0; idx < rowItems.length; idx++) {
				const ri = rowItems[idx];
				const colX = x + idx * (colW + SP.blockGap);
				doc.setFont("helvetica", "bold");
				doc.setFontSize(labelFont);
				doc.setTextColor(...theme.muted);
				doc.text(`${safeText(ri.label, "")}:`, colX + padding, cursorY + 12);

				doc.setFont("helvetica", "normal");
				doc.setFontSize(valueFont);
				doc.setTextColor(...theme.text);
				doc.text(ri._lines, colX + padding, cursorY + 26);
			}

			cursorY += rowH + rowGap;
		}

		yPosition += h + SP.blockGap;
	};

	// corners out
	drawSectionHeader(
		t?.("goalkeeper_report.sections.set_piece_corners_out") ||
			"Palle inattive - Corner a uscire",
	);
	drawViewFieldGrid({
		items: [
			{
				label: t?.("goalkeeper_report.view_fields.corner_out_goalkeeper_exit"),
				value: reportData?.cornerOutIntervention,
			},
			{
				label: t?.("goalkeeper_report.view_fields.corner_out_save_direct"),
				value: reportData?.cornerOutSave,
			},
			{
				label: t?.(
					"goalkeeper_report.view_fields.corner_out_defended_by_teammates",
				),
				value: reportData?.cornerOutDefendedTeammate,
			},
			{
				label: t?.(
					"goalkeeper_report.view_fields.corner_out_shot_hit_by_opponents_no_goal",
				),
				value: reportData?.cornerOutOpponents,
			},
			{
				label: t?.("goalkeeper_report.view_fields.corner_out_goal_direct"),
				value: reportData?.cornerOutGoal,
			},
		],
	});

	// corners in
	drawSectionHeader(
		t?.("goalkeeper_report.sections.set_piece_corners_in") ||
			"Palle inattive - Corner a rientrare",
	);
	drawViewFieldGrid({
		items: [
			{
				label: t?.("goalkeeper_report.view_fields.corner_in_goalkeeper_exit"),
				value: reportData?.cornerRetIntervention,
			},
			{
				label: t?.("goalkeeper_report.view_fields.corner_in_save_direct"),
				value: reportData?.cornerRetSave,
			},
			{
				label: t?.(
					"goalkeeper_report.view_fields.corner_in_defended_by_teammates",
				),
				value: reportData?.cornerRetDefendedTeammate,
			},
			{
				label: t?.(
					"goalkeeper_report.view_fields.corner_in_shot_hit_by_opponents_no_goal",
				),
				value: reportData?.cornerRetOpponents,
			},
			{
				label: t?.("goalkeeper_report.view_fields.corner_in_goal_direct"),
				value: reportData?.cornerRetGoal,
			},
		],
	});

	// set piece metrics
	drawSectionHeader(
		t?.("goalkeeper_report.sections.set_piece_metrics") ||
			"Palle inattive - Metriche",
	);
	drawViewFieldGrid({
		items: [
			{
				label: t?.("goalkeeper_report.view_fields.corner_metrics_man_to_man"),
				value: reportData?.manToManMarking,
			},
			{
				label: t?.("goalkeeper_report.view_fields.corner_metrics_zone"),
				value: reportData?.zonalMarking,
			},
			{
				label: t?.("goalkeeper_report.view_fields.corner_metrics_mixed"),
				value: reportData?.mixedMarking,
			},
			{
				label: t?.("goalkeeper_report.view_fields.corner_metrics_blocked"),
				value: reportData?.blockedCorners,
			},
			{
				label: t?.("goalkeeper_report.view_fields.corner_metrics_short_taken"),
				value: reportData?.shortCorners,
			},
			{
				label: t?.("goalkeeper_report.view_fields.corner_metrics_area_attacks"),
				value: reportData?.totalAttacksArea,
			},
		],
	});

	// =========================
	// Coach notes
	// =========================
	drawSectionHeader(t?.("goalkeeper_report.sections.info_report"));
	// strengths + improvements + free notes (drawCoachNotes already includes section headers)
	drawCoachNotes();

	// =========================
	// Final summary
	// =========================
	drawSectionHeader(
		t?.("goalkeeper_report.sections.final_summary") || "Riepilogo finale",
	);

	const totalOffense =
		parseInt(reportData?.totalOffensiveGameOk || 0, 10) +
		parseInt(reportData?.totalOffensiveGameFailed || 0, 10);
	const totalDefense =
		parseInt(reportData?.totalDefensiveGameOk || 0, 10) +
		parseInt(reportData?.totalDefensiveGameFailed || 0, 10);
	const totalVolume = totalOffense + totalDefense;
	const totalCorners = reportData?.totalCorners;

	const finalItems = [
		{
			label: t?.("goalkeeper_report.final_summary.offense_total"),
			value: totalOffense,
		},
		{
			label: t?.("goalkeeper_report.final_summary.defense_total"),
			value: totalDefense,
		},
		{
			label: t?.("goalkeeper_report.final_summary.volume_total"),
			value: totalVolume,
		},
		{
			label: t?.("goalkeeper_report.final_summary.corners_conceded_total"),
			value: totalCorners,
		},
	];

	const gridX = SP.pagePad;
	const gridW = pageWidth - SP.pagePad * 2;
	const colGap = SP.blockGap;
	const cellW = (gridW - colGap) / 2;
	const innerPad = 10;
	const labelFont = 9;
	const valueFont = 16;
	const rowGap = SP.blockGap;

	const calcBoxH = (label, value) => {
		const labelLines = wrapText(
			safeText(label, ""),
			cellW - innerPad * 2,
			labelFont,
		);
		const valueLines = wrapText(
			safeText(value, naPlaceholder),
			cellW - innerPad * 2,
			valueFont,
		);
		const lh = getTextLinesHeight(labelLines.length, labelFont, 1.15);
		const vh = getTextLinesHeight(valueLines.length, valueFont, 1.0);
		return innerPad * 2 + lh + 8 + vh + 8;
	};

	const boxH = Math.max(
		...finalItems.map((it) => calcBoxH(it.label, it.value)),
	);

	checkPageBreak(boxH * 2 + rowGap + 10);

	for (let i = 0; i < finalItems.length; i++) {
		const it = finalItems[i];
		const col = i % 2;
		const row = Math.floor(i / 2);
		const bx = gridX + col * (cellW + colGap);
		const by = yPosition + row * (boxH + rowGap);

		drawCard(bx, by, cellW, boxH, {radius: 12, shadow: true});

		doc.setFont("helvetica", "bold");
		doc.setFontSize(labelFont);
		doc.setTextColor(...theme.primaryDark);
		const labelLines = wrapText(
			safeText(it.label, ""),
			cellW - innerPad * 2,
			labelFont,
		);
		doc.text(labelLines, bx + innerPad, by + innerPad + 12);

		doc.setFont("helvetica", "bold");
		doc.setFontSize(valueFont);
		doc.setTextColor(...theme.text);
		const valueLines = wrapText(
			safeText(it.value, naPlaceholder),
			cellW - innerPad * 2,
			valueFont,
		);
		const labelH = getTextLinesHeight(labelLines.length, labelFont, 1.15);
		doc.text(valueLines, bx + innerPad, by + innerPad + 12 + labelH + 6);
	}

	yPosition += boxH * 2 + rowGap;

	// =========================
	// FOOTER (clean)
	// =========================
	const totalPages = doc.internal.pages.length - 1;
	const generatedAt = `Generato il ${moment().format("DD/MM/YYYY • HH:mm")}`;

	for (let i = 1; i <= totalPages; i++) {
		doc.setPage(i);
		doc.setDrawColor(...theme.border);
		doc.setLineWidth(1);
		doc.line(
			SP.pagePad,
			pageHeight - SP.footerH + 18,
			pageWidth - SP.pagePad,
			pageHeight - SP.footerH + 18,
		);

		doc.setFont("helvetica", "normal");
		doc.setFontSize(9);
		doc.setTextColor(...theme.muted);
		doc.text(generatedAt, SP.pagePad, pageHeight - 18);
		doc.text(
			`Pagina ${i} / ${totalPages}`,
			pageWidth - SP.pagePad,
			pageHeight - 18,
			{
				align: "right",
			},
		);
	}

	const goalkeeperNameSafe = goalkeeperName
		? goalkeeperName.replace(/[\\/:*?"<>|]/g, "-")
		: t?.("goalkeeper_report.header.goalkeeper") || "Portiere";
	const reportDateSafe = dateText
		? dateText.replace(/[\\/:*?"<>|]/g, "-")
		: "N-A";
	const safePrefix = (
		fileNamePrefix ||
		t?.("goalkeeper_report.export_title") ||
		"Report"
	).replace(/[\\/:*?"<>|]/g, "-");

	const finalFileName = `${safePrefix}_${goalkeeperNameSafe}_${reportDateSafe}.pdf`;
	doc.save(finalFileName);
};
